import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { Toaster } from "@/components/ui/sonner";

import LoginPage from "@/pages/LoginPage";
import Layout from "@/components/Layout";
import DashboardPage from "@/pages/DashboardPage";
import ClientsPage from "@/pages/ClientsPage";
import ClientDetailPage from "@/pages/ClientDetailPage";
import CalendarPage from "@/pages/CalendarPage";
import ApprovalsPage from "@/pages/ApprovalsPage";
import CommissionsPage from "@/pages/CommissionsPage";
import BuchhaltungPage from "@/pages/BuchhaltungPage";
import KonsulentenAnagraficaPage from "@/pages/KonsulentenAnagraficaPage";
import SuperAdminDashboardPage from "@/pages/SuperAdminDashboardPage";
import AntraegeBoardPage from "@/pages/AntraegeBoardPage";
import FinancePage from "@/pages/FinancePage";
import CEOPage from "@/pages/CEOPage";
import MobileSignaturePage from "@/pages/MobileSignaturePage";
import CompaniesPage from "@/pages/CompaniesPage";
import ProviderManagementPage from "@/pages/ProviderManagementPage";
import UsersPage from "@/pages/UsersPage";
import DossierPage from "@/pages/DossierPage";
import MobileUploadPage from "@/pages/MobileUploadPage";
import TasksPage from "@/pages/TasksPage";
import ChatPage from "@/pages/ChatPage";
import AIComplianceePage from "@/pages/AIComplianceePage";
import SystemHealthPage from "@/pages/SystemHealthPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import SecurityPage from "@/pages/SecurityPage";
import DangerZonePage from "@/pages/DangerZonePage";
import AutoTextTranslator from "@/components/AutoTextTranslator";
import { ThemeProvider } from "@/contexts/ThemeContext";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-sm">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function normalizeRole(r) {
  return { admin: "master", consultant: "basic" }[r] || r || "basic";
}

function RoleGuard({ allow, children }) {
  const { user } = useAuth();
  const role = normalizeRole(user?.role);
  if (!allow.includes(role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AdminOnly({ children }) {
  return <RoleGuard allow={["master"]}>{children}</RoleGuard>;
}

function NotFinanza({ children }) {
  return <RoleGuard allow={["master", "control", "basic"]}>{children}</RoleGuard>;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? "/dashboard" : "/login"} replace />;
}

export default function App() {
  return (
    <I18nProvider>
      <ThemeProvider>
        <AuthProvider>
          <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/m/u/:sessionId" element={<MobileUploadPage />} />
            <Route path="/m/sign/:sessionId" element={<MobileSignaturePage />} />
            <Route path="/sign/:sessionId" element={<MobileSignaturePage />} />
            <Route element={<Protected><Layout /></Protected>}>
              <Route path="/dashboard" element={<DashboardPage />} />
              <Route path="/clients" element={<NotFinanza><ClientsPage /></NotFinanza>} />
              <Route path="/clients/:id" element={<NotFinanza><ClientDetailPage /></NotFinanza>} />
              <Route path="/calendar" element={<NotFinanza><CalendarPage /></NotFinanza>} />
              <Route path="/clients/:id/dossier/*" element={<NotFinanza><DossierPage /></NotFinanza>} />
              <Route path="/companies" element={<NotFinanza><CompaniesPage /></NotFinanza>} />
              <Route path="/anbieter-verwaltung" element={<AdminOnly><ProviderManagementPage /></AdminOnly>} />
              <Route path="/genehmigtls" element={<RoleGuard allow={["master", "control"]}><ApprovalsPage /></RoleGuard>} />
              <Route path="/commissions" element={<CommissionsPage />} />
              <Route path="/buchhaltung" element={<AdminOnly><BuchhaltungPage /></AdminOnly>} />
              <Route path="/konsulenten-anagrafica" element={<AdminOnly><KonsulentenAnagraficaPage /></AdminOnly>} />
              <Route path="/super-admin" element={<AdminOnly><SuperAdminDashboardPage /></AdminOnly>} />
              <Route path="/antraege-board" element={<AntraegeBoardPage />} />
              <Route path="/finance" element={<RoleGuard allow={["master", "finanza"]}><FinancePage /></RoleGuard>} />
              <Route path="/ceo" element={<AdminOnly><CEOPage /></AdminOnly>} />
              <Route path="/users" element={<AdminOnly><UsersPage /></AdminOnly>} />
              <Route path="/security" element={<SecurityPage />} />
              <Route path="/tasks" element={<TasksPage />} />
              <Route path="/chat" element={<ChatPage />} />
              <Route path="/compliance" element={<NotFinanza><AIComplianceePage /></NotFinanza>} />
              <Route path="/system-health" element={<AdminOnly><SystemHealthPage /></AdminOnly>} />
              <Route path="/admin/danger-zone" element={<AdminOnly><DangerZonePage /></AdminOnly>} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" duration={2000} offset={70} />
          <AutoTextTranslator />
        </BrowserRouter>
        </AuthProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}
